import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { LayoutDashboard, FileText, BookOpen, Boxes, Award, GraduationCap, Mic2, Image as ImageIcon, MessageSquare, Settings, LogOut, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const SECTIONS = [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/admin/content/home", label: "Home Content", icon: FileText },
    { to: "/admin/content/about", label: "About", icon: FileText },
    { to: "/admin/content/research", label: "Research", icon: FileText },
    { to: "/admin/content/teaching", label: "Teaching", icon: FileText },
    { to: "/admin/content/cv", label: "CV", icon: FileText },
    { to: "/admin/content/contact", label: "Contact", icon: FileText },
    { to: "/admin/publications", label: "Publications", icon: BookOpen },
    { to: "/admin/projects", label: "Projects", icon: Boxes },
    { to: "/admin/awards", label: "Awards", icon: Award },
    { to: "/admin/courses", label: "Courses", icon: GraduationCap },
    { to: "/admin/conferences", label: "Conferences", icon: Mic2 },
    { to: "/admin/media", label: "Media", icon: ImageIcon },
    { to: "/admin/messages", label: "Messages", icon: MessageSquare },
    { to: "/admin/settings", label: "Settings", icon: Settings },
];

export default function AdminLayout() {
    const { logout, user } = useAuth();
    const nav = useNavigate();

    return (
        <div className="dark min-h-screen bg-background text-foreground">
            <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] min-h-screen">
                <aside className="hidden lg:flex flex-col border-r border-white/10 bg-black/20">
                    <div className="px-5 py-6">
                        <p className="font-display text-2xl">Aditya Baheti</p>
                        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-paper/50 mt-1">Admin · v1.0</p>
                    </div>
                    <nav className="flex-1 overflow-y-auto px-3 pb-4 space-y-0.5" aria-label="Admin sections">
                        {SECTIONS.map((s) => {
                            const Icon = s.icon;
                            return (
                                <NavLink
                                    key={s.to}
                                    to={s.to}
                                    end={s.end}
                                    className={({ isActive }) => `relative flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${isActive ? "bg-white/[0.08] text-paper" : "text-paper/70 hover:text-paper hover:bg-white/[0.04]"}`}
                                    data-testid={`admin-nav-${s.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                >
                                    {({ isActive }) => (
                                        <>
                                            {isActive && <motion.span layoutId="admin-nav-active" className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r bg-[hsl(var(--sea-glass))]" />}
                                            <Icon className="h-4 w-4" />
                                            <span>{s.label}</span>
                                        </>
                                    )}
                                </NavLink>
                            );
                        })}
                    </nav>
                    <div className="px-3 py-4 border-t border-white/10 space-y-2">
                        <Link to="/" target="_blank" className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-paper/70 hover:text-paper hover:bg-white/[0.04]" data-testid="admin-view-site-link">
                            <ExternalLink className="h-4 w-4" /> View site
                        </Link>
                        <Button variant="ghost" onClick={() => { logout(); nav("/admin/login"); }} className="w-full justify-start text-paper/80 hover:text-paper hover:bg-white/[0.06]" data-testid="admin-logout-button">
                            <LogOut className="h-4 w-4 mr-2" /> Sign out
                        </Button>
                    </div>
                </aside>

                <div className="flex flex-col min-h-screen">
                    <div className="lg:hidden sticky top-0 z-30 bg-ink/95 backdrop-blur border-b border-white/10 px-4 py-3 flex items-center justify-between">
                        <p className="font-display text-lg">Admin</p>
                        <Button size="sm" variant="ghost" onClick={() => { logout(); nav("/admin/login"); }} className="text-paper">Sign out</Button>
                    </div>
                    <header className="hidden lg:flex items-center justify-between px-8 py-5 border-b border-white/10 bg-black/10">
                        <div>
                            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-paper/50">Signed in as</p>
                            <p className="text-sm">{user?.username}</p>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button asChild variant="ghost" className="text-paper/80 hover:text-paper hover:bg-white/[0.06]">
                                <Link to="/" target="_blank"><ExternalLink className="h-4 w-4 mr-2" /> View site</Link>
                            </Button>
                        </div>
                    </header>

                    <main className="flex-1 p-6 lg:p-10 overflow-x-hidden">
                        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}>
                            <Outlet />
                        </motion.div>
                    </main>

                    {/* Mobile bottom nav */}
                    <nav className="lg:hidden sticky bottom-0 z-30 bg-ink/95 backdrop-blur border-t border-white/10 px-2 py-2 overflow-x-auto">
                        <div className="flex gap-1 min-w-max">
                            {SECTIONS.map((s) => (
                                <NavLink key={s.to} to={s.to} end={s.end} className={({ isActive }) => `px-3 py-2 text-xs rounded-lg whitespace-nowrap ${isActive ? "bg-white/[0.08] text-paper" : "text-paper/60"}`}>
                                    {s.label}
                                </NavLink>
                            ))}
                        </div>
                    </nav>
                </div>
            </div>
        </div>
    );
}
